import { useInfiniteQuery } from "@tanstack/react-query";
import { regionService } from "../api/services/regionService";
import type {
  Region,
  RegionFilters,
  RegionsResponse,
} from "../schemas/regionSchema";
import { useRegions } from "./useRegions";
import { DEFAULT_PAGE_SIZE } from "../constants";

export type RegionsInfiniteFilters = Omit<
  RegionFilters,
  "page" | "pageSize"
> & {
  pageSize?: number;
};

export const useRegionsInfinite = (filters: RegionsInfiniteFilters = {}) => {
  const { pageSize = DEFAULT_PAGE_SIZE, ...rest } = filters;
  const mutations = useRegions(undefined, { enabled: false });

  const query = useInfiniteQuery<RegionsResponse, Error>({
    queryKey: ["regions", "infinite", { ...rest, pageSize }],
    queryFn: ({ pageParam }) =>
      regionService.getAll({
        ...rest,
        page: pageParam as number,
        pageSize,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.hasNextPage ? (lastPage.page ?? 0) + 1 : undefined,
  });

  const regions: Region[] = query.data?.pages.flatMap((p) => p.regions) ?? [];
  const total = query.data?.pages[0]?.total ?? 0;

  return {
    ...mutations,
    regions,
    total,
    isLoading: query.isLoading || mutations.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: !!query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
  };
};
